<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { debounce } from "vue-debounce";
import { useBasePriceStore } from "@/modules/base_price/store";
import { matchesHierarchyFilter } from "@/modules/base_price/utils/hierarchyFilter";
import PlanHierarchyRow, {
	HierarchyTreeNode,
} from "@/modules/base_price/components/PlanHierarchyRow.vue";

const store = useBasePriceStore();

/**
 * A node's own screens (see ResolvedNodeDTO.assets) as child rows under it,
 * for every node that carries any — a screen is an editable target in its
 * own right (its own seasonality, fill and CPM, see ResolvedAssetDTO), so it
 * has to be reachable in the tree the same way a node is. Negative ids keep
 * these rows from colliding with a real hierarchy_node_id; `assetId` carries
 * the real Asset id, which lives in its own id space.
 *
 * Every figure below is the resolver's own, not a reconstruction: an earlier
 * version approximated a screen's monthly curves by scaling its node's
 * arrays down by the screen's share, because the backend only sent annual
 * totals per screen. It now prices each screen month by month against its
 * own rates, so scaling would actively disagree with it.
 */
const assetsAsTreeNodes = (node: HierarchyTreeNode): HierarchyTreeNode[] =>
	store.assetsOf(node).map((asset) => ({
		hierarchy_node_id: -asset.asset_id,
		assetId: asset.asset_id,
		// Never shown, only searched — see matchesHierarchyFilter().
		nameClean: asset.name_clean,
		parent_id: node.hierarchy_node_id,
		name: asset.name,
		budget: asset.budget,
		allotted_budget: asset.budget,
		gross_impressions: asset.gross_impressions,
		sellable_impressions: asset.sellable_impressions,
		revenue: asset.revenue,
		cpm: asset.cpm,
		derived_cpm: asset.derived_cpm,
		split_own: false,
		fixed_budget_own: asset.fixed_budget_own,
		manual_cpm_own: asset.manual_cpm_own,
		budget_fully_allocated: true,
		monthly_season_shares: asset.monthly_season_shares,
		monthly_season_weights: asset.monthly_season_weights,
		monthly_season_locks: asset.monthly_season_locks,
		monthly_budget_pins: asset.monthly_budget_pins,
		month_budget_own: asset.month_budget_own,
		season_fully_allocated: asset.season_fully_allocated,
		days_own: asset.days_own,
		days_fully_allocated: asset.days_fully_allocated,
		day_pricing_own: asset.day_pricing_own,
		season_own: asset.season_own,
		season_source_node_id: null,
		monthly_fill_rates: asset.monthly_fill_rates,
		fill_own: asset.fill_own,
		monthly_manual_cpm: asset.monthly_manual_cpm,
		monthly_impression_share: asset.monthly_impression_share,
		impression_share_own: asset.impression_share_own,
		cpm_min: asset.cpm_min,
		cpm_max: asset.cpm_max,
		cpm_bounds_own: asset.cpm_bounds_own,
		cpm_within_bounds: asset.cpm_within_bounds,
		monthly_cpm_clamped: asset.monthly_cpm_clamped,
		monthly_budget: asset.monthly_budget,
		monthly_gross_impressions: asset.monthly_gross_impressions,
		monthly_sellable_impressions: asset.monthly_sellable_impressions,
		monthly_revenue: asset.monthly_revenue,
		assets: [],
		children: [],
		isAsset: true,
	}));

const tree = computed<HierarchyTreeNode[]>(() => {
	const byId = new Map<number, HierarchyTreeNode>();
	store.selectedBookNodes.forEach((node) =>
		byId.set(node.hierarchy_node_id, { ...node, children: [] })
	);

	const roots: HierarchyTreeNode[] = [];
	byId.forEach((node) => {
		const parent = node.parent_id !== null ? byId.get(node.parent_id) : null;
		if (parent) {
			parent.children.push(node);
		} else {
			roots.push(node);
		}
	});

	byId.forEach((node) => {
		node.children.push(...assetsAsTreeNodes(node));
	});

	return roots;
});

/**
 * What the filter field holds. While it holds anything, the tree shows only
 * the nodes and screens whose name contains it — and the levels above them,
 * so each match is seen where it sits — all opened up.
 */
const filterText = ref<string | null>("");

/**
 * What the tree is actually filtered on: the field's text once typing pauses,
 * and only from two characters on — a single letter matches nearly every
 * name, and rebuilding the tree for each keystroke is the expensive part on a
 * portfolio of thousands of screens. Clearing the field applies at once.
 */
const MIN_QUERY_LENGTH = 2;
const query = ref("");

const fieldText = () => (filterText.value ?? "").trim().toLocaleLowerCase();

// Reads the field when it fires rather than when it was scheduled, so a
// filter cleared while one was pending is never brought back by it.
const applyQuery = () => {
	const text = fieldText();

	query.value = text.length >= MIN_QUERY_LENGTH ? text : "";
};

const applyQueryLater = debounce(applyQuery, 300);

watch(filterText, () => (fieldText() === "" ? applyQuery() : applyQueryLater()));

const matchesQuery = (node: HierarchyTreeNode) => matchesHierarchyFilter(node, query.value);

/**
 * The tree cut down to the matches and their ancestors. A match keeps only
 * the descendants that match too: "only the nodes or screens that match" is
 * the point, and a matching group with a few hundred screens under it would
 * otherwise bury the rest.
 */
const prune = (nodes: HierarchyTreeNode[]): HierarchyTreeNode[] =>
	nodes.flatMap((node) => {
		const children = prune(node.children);

		return matchesQuery(node) || children.length ? [{ ...node, children }] : [];
	});

const visibleTree = computed(() => (query.value ? prune(tree.value) : tree.value));

const matchCount = computed(() => {
	const count = (nodes: HierarchyTreeNode[]): number =>
		nodes.reduce((sum, node) => sum + (matchesQuery(node) ? 1 : 0) + count(node.children), 0);

	return query.value ? count(visibleTree.value) : 0;
});

const leafCount = computed(
	() =>
		store.selectedBookNodes.filter(
			(node) =>
				!store.selectedBookNodes.some((n) => n.parent_id === node.hierarchy_node_id)
		).length
);

type Metric = "budget" | "cpm";
const metric = ref<Metric>("budget");
const metricOptions: { value: Metric; label: string }[] = [
	{ value: "budget", label: "Budget" },
	{ value: "cpm", label: "CPM" },
];

const columnHeaders = computed(() =>
	metric.value === "budget" ? ["Budget", "CPM", "%"] : ["Avg", "Min", "Max"]
);
</script>

<template>
	<div v-if="store.selectedBook" class="d-flex flex-column h-100">
		<div class="d-flex align-baseline justify-space-between px-3 pt-3 pb-2">
			<div
				class="text-caption text-uppercase text-medium-emphasis text-truncate"
			>
				{{ store.selectedBook.name }}
			</div>
			<div class="text-caption text-medium-emphasis flex-shrink-0 ml-2">
				{{ query ? `${matchCount} ${matchCount === 1 ? "match" : "matches"}` : `${leafCount} nodes` }}
			</div>
		</div>

		<div class="d-flex align-center ga-2 px-3 pb-3">
			<span class="text-caption text-medium-emphasis flex-shrink-0">Metrics</span>
			<div class="metric-toggle flex-grow-1">
				<div
					v-for="option in metricOptions"
					:key="option.value"
					class="metric-toggle-option"
					:class="{ 'metric-toggle-option--active': metric === option.value }"
					@click="metric = option.value"
				>
					{{ option.label }}
				</div>
			</div>
		</div>

		<div class="px-3 pb-3">
			<v-text-field
				v-model="filterText"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				placeholder="Filter nodes and screens…"
				clearable
				hide-details
				variant="filled"
				density="compact"
			/>
		</div>

		<div
			class="tree-header d-flex align-center ga-2 px-3 py-2 text-caption text-uppercase text-medium-emphasis"
		>
			<!-- Matches .tree-label in PlanHierarchyRow.vue: same 310px cap, and
			     34px of lead-in (12px row padding + the 14px twisty + its 8px
			     gap) so the header sits over the names, not the twisties. -->
			<span class="tree-label-header">Level</span>
			<span
				v-for="header in columnHeaders"
				:key="header"
				class="tree-col-value"
				:class="{ 'tree-col-value--narrow': metric === 'cpm' || header === '%' }"
			>
				{{ header }}
			</span>
		</div>

		<perfect-scrollbar class="flex-grow-1" :options="{ suppressScrollX: true }">
			<plan-hierarchy-row
				v-for="node in visibleTree"
				:key="node.hierarchy_node_id"
				:node="node"
				:depth="0"
				:metric="metric"
				:filter="query"
			/>
			<div
				v-if="query && !visibleTree.length"
				class="text-caption text-medium-emphasis px-3 py-4"
			>
				No nodes or screens match "{{ filterText }}".
			</div>
		</perfect-scrollbar>

		<div class="legend d-flex flex-column ga-2 px-3 py-3 text-caption text-medium-emphasis">
			<div class="d-flex align-center ga-2">
				<span class="legend-dot" style="background: rgb(var(--v-theme-warning))" />
				Override in this book
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.tree-label-header {
	flex: 1 1 auto;
	min-width: 0;
	max-width: 310px;
	padding-left: 34px;
}

.tree-header {
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	background: rgba(var(--v-theme-on-surface), 0.02);
}

.tree-col-value {
	flex: 0 0 72px;
	text-align: right;
}

.tree-col-value--narrow {
	flex: 0 0 56px;
}

.metric-toggle {
	display: flex;
	gap: 2px;
	padding: 3px;
	border-radius: 7px;
	background: rgba(var(--v-theme-on-surface), 0.06);
}

.metric-toggle-option {
	flex: 1;
	text-align: center;
	padding: 4px 10px;
	border-radius: 5px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
}

.metric-toggle-option--active {
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.legend {
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.legend-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	flex: 0 0 auto;
}
</style>
